import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { i18nLanguage } from "i18n";
import { DistanceUnit } from "shared/types";
import {
  TripperData,
  StopBlock,
  OperatorRecord,
} from "../../types";
import { flattenNavigableItems, NavigableStop } from "../../utils/flattenNavigableItems";
import { useCarouselSync } from "../../hooks/useCarouselSync";
import { useTripperScrollContext } from "../../context/TripperWidgetContext";
import { findGroupBlock } from "../../utils/groupHelpers";
import { TripperMap } from "../TripperMap";
import { CarouselStopCard } from "./CarouselStopCard";
import { CarouselGroupLabel } from "./CarouselGroupLabel";

type MobileMapCarouselProps = {
  data: TripperData;
  language: i18nLanguage;
  unit: DistanceUnit;
  onStopClick: (stop: StopBlock, operator: OperatorRecord, markerNumber: number) => void;
  onMarkerClick?: (itemId: string) => void;
};

export const MobileMapCarousel = ({
  data,
  language,
  unit,
  onStopClick,
  onMarkerClick,
}: MobileMapCarouselProps) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);

  // Flatten all items, then keep only stops for the carousel
  const allItems = useMemo(
    () => flattenNavigableItems(data.segments, data.operatorByEntityId),
    [data.segments, data.operatorByEntityId],
  );

  const stopItems = useMemo(
    () => allItems.filter((item): item is NavigableStop => item.type === "stop"),
    [allItems],
  );

  // Build a distance-to-next lookup: for each stop, find the next stop's distanceFromPrev
  const distanceToNext = useMemo(() => {
    const map: Record<string, { meters: number; seconds: number }> = {};
    for (let i = 0; i < stopItems.length - 1; i++) {
      const nextStop = stopItems[i + 1].stop;
      const meters = nextStop.distanceFromPrev ?? 0;
      const seconds = nextStop.timeFromPrev ?? 0;
      if (meters > 0 || seconds > 0) {
        map[stopItems[i].stop._id] = { meters, seconds };
      }
    }
    return map;
  }, [stopItems]);

  const { scrollToItem } = useCarouselSync({
    containerRef: carouselRef,
    items: stopItems,
    enabled: true,
  });

  const { activeItemId, activeStopGroupId, setIsMetadataInView } = useTripperScrollContext();

  // Scroll the carousel to the stop that was active in the list view
  const initialItemRef = useRef(activeItemId);
  useLayoutEffect(() => {
    const itemId = initialItemRef.current;
    if (!itemId) return;
    const container = carouselRef.current;
    if (!container) return;

    const target = container.querySelector<HTMLElement>(`[data-item-id="${itemId}"]`);
    if (!target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const scrollLeft =
      container.scrollLeft +
      (targetRect.left + targetRect.width / 2) -
      (containerRect.left + containerRect.width / 2);

    container.scrollTo({ left: scrollLeft, behavior: "instant" });
  }, []);

  // In carousel mode there's no header/metadata section — tell auto-follow
  // to skip the overview and fly to individual stops.
  useEffect(() => {
    setIsMetadataInView(false);
  }, [setIsMetadataInView]);

  const activeGroup = useMemo(
    () => findGroupBlock(data.segments, activeStopGroupId) ?? null,
    [activeStopGroupId, data.segments],
  );

  const handleMarkerClick = useCallback(
    (itemId: string) => {
      (onMarkerClick ?? scrollToItem)(itemId);
    },
    [onMarkerClick, scrollToItem],
  );

  const handleCardClick = useCallback(
    (item: NavigableStop) => {
      if (activeItemId === item.stop._id) {
        // Focused card → open info panel
        onStopClick(item.stop, item.operator, item.markerNumber);
      } else {
        // Non-focused card → scroll to it
        scrollToItem(item.stop._id);
      }
    },
    [activeItemId, onStopClick, scrollToItem],
  );

  return (
    <div className="tripper-mobile-carousel-root">
      {/* Full-screen map */}
      <TripperMap data={data} onMarkerClick={handleMarkerClick} hideAutoFollow hideControls />

      {/* Floating group label */}
      <CarouselGroupLabel group={activeGroup} language={language} />

      {/* Bottom carousel strip */}
      <div ref={carouselRef} className="tripper-carousel">
        {/* Leading spacer so first card can center */}
        <div className="tripper-carousel__spacer" />

        {stopItems.map((item, index) => (
          <div
            key={item.stop._id}
            className="tripper-carousel__card-wrapper"
            data-carousel-index={index}
            data-item-id={item.stop._id}
          >
            <CarouselStopCard
              stop={item.stop}
              operator={item.operator}
              language={language}
              markerNumber={item.markerNumber}
              isActive={activeItemId === item.stop._id}
              distanceToNext={distanceToNext[item.stop._id]}
              unit={unit}
              onClick={() => handleCardClick(item)}
            />
          </div>
        ))}

        {/* Trailing spacer so last card can center */}
        <div className="tripper-carousel__spacer" />
      </div>
    </div>
  );
};
